import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { Component, ElementRef, Inject, makeStateKey, PLATFORM_ID, TransferState, ViewChild } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Modal, ModalInterface, ModalOptions } from 'flowbite';
import { BehaviorSubject, Observable, concatMap, of, switchMap } from 'rxjs';
import { PageService } from 'src/app/core/page/page.service';
import { JobsDto, RawFormJobObject } from 'src/app/data/dtos';
import { successJobsDataSelector } from 'src/app/store/jobs/selectors';
import { selectActiveLanguage } from 'src/app/store/language/selectors';
import { AppStateInterface } from 'src/app/store/types';
import { NgFor, AsyncPipe, isPlatformServer } from '@angular/common';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { SafeHtmlPipe } from 'src/app/core/custom-pipes/safeHtml.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { MapComponent } from 'src/app/shared/map/map.component';
import { BrowserService } from 'src/app/core/browser.service';
import { heroBanknotes } from '@ng-icons/heroicons/outline';
import { bootstrapBriefcase, bootstrapGeoAltFill, bootstrapGlobe2 } from '@ng-icons/bootstrap-icons';
import { ActivatedRoute, Router } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { log } from 'console';

const JOB_DETAILS_KEY = makeStateKey<any>('selectedJobData');

@Component({
    selector: 'app-job',
    templateUrl: './job.component.html',
    styleUrl: './job.component.scss',
    animations: [
        trigger('animateMoadl', [
            state('false', style({
                opacity: 0,
                transform: 'translateY(5%)',
            })),
            state('true', style({
                opacity: 1,
                transform: 'translateY(0%)',
            })),
            transition('false => true', animate('300ms ease-in')),
        ]),
    ],
    imports: [
        NgFor,
        NgIcon,
        MapComponent,
        AsyncPipe,
        SafeHtmlPipe,
        TranslateModule,
    ],
    providers: [
        provideIcons({
            bootstrapGeoAltFill,
            bootstrapBriefcase,
            bootstrapGlobe2,
            heroBanknotes
        })
    ]
})
export class JobComponent {
  @ViewChild('loginOrRegisterModal', { static: false })
  loginOrRegisterModalElement!: ElementRef;
  loginOrRegisterModal!: ModalInterface;

  private readonly companyLogo: string = 'company_logo';
  private readonly jobCategories: string = 'job_categories';

  currentJob$: BehaviorSubject<RawFormJobObject | null> =
    new BehaviorSubject<RawFormJobObject | null>(null);
  activeLanguage: string = 'en';
  languageDirection: string = 'ltr';

  selectedJobSlug: string = '';
  animateState: boolean = false;

  constructor(
    private _pageService: PageService,
    private _store: Store<AppStateInterface>,
    private browserService: BrowserService,
    private _router: Router,
    private activatedRoute: ActivatedRoute,
    private transferState: TransferState,
    private titleService: Title,
    private metaService: Meta,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    this.selectedJobSlug = this._router.url.split('/').pop() ?? '';
    //fetch data on the server side and use the cached data on the client side
    if (isPlatformServer(this.platformId)) {
      this.getJobFromServer().subscribe({
        next: jobData => {
          if (jobData && jobData.data.length > 0) {
            for (let job of jobData.data) {
              if (job.attributes.slug === this.selectedJobSlug) {
                this.currentJob$.next(job);
                this.transferState.set(JOB_DETAILS_KEY, job);
                this.initJobSEOContent(job)
              }
            }
          }
        },
        error: error => {
          console.log("could not get the job on the server side")
        }
      })
    } else {
      this._store.select(selectActiveLanguage).subscribe({
        next: (currentLanguage) => {
          this.activeLanguage = currentLanguage;
          this.languageDirection = this._pageService.getLanguageDirection(currentLanguage);
          this.getSelectedjobObservable().subscribe({
            next: (job) => {
              if(job){
                this.initJobSEOContent(job)
                this.currentJob$.next(job);
              }
            },
          });
        },
      });
      // // Use cached data on the client
      // this.jobDetails = this.transferState.get(JOB_DETAILS_KEY, null);
    }
  }

  ngAfterViewInit() {
    this._pageService.setDirection(this.activeLanguage);
    if(this.browserService.isBrowserPlatform()){
      this.initloginOrRegisterModalElement(
        this.loginOrRegisterModalElement.nativeElement,
      );
    }
  }

  showLoginOrRegisterModal() {
    this.loginOrRegisterModal.show();
  }

  private initloginOrRegisterModalElement(element: any): void {
    const modalOptions: ModalOptions = {
      placement: 'bottom-right',
      backdrop: 'dynamic',
      backdropClasses: 'bg-gray-900/50 dark:bg-gray-900/80 fixed inset-0 z-40',
      closable: true,
      onHide: () => {
        this.animateState = false;
      },
      onShow: () => {
        this.animateState = true;
      },
      onToggle: () => {},
    };
    const instanceOptions = {
      id: 'modalDialog',
      override: true,
    };
    this.loginOrRegisterModal = new Modal(
      element,
      modalOptions,
      instanceOptions,
    );
  }

  closeLoginOrRegisterModal(event?: KeyboardEvent) {
    if (event && event.key === 'Escape') {
      this.loginOrRegisterModal.hide();
    } else if (!event) {
      this.loginOrRegisterModal.hide();
    }
  }

  private getSelectedjobObservable(): Observable<RawFormJobObject | null> {
    return this._store.pipe(
      select(successJobsDataSelector),
      concatMap((jobs) => {
        // if the job is saved in store
        if (jobs && jobs.data.length > 0) {
          for (let job of jobs.data) {
            if (job.attributes.slug === this.selectedJobSlug) {
              return of(job);
            }
          }
          return of(null);
        } else {
          // get the job from cms using slug
          return this.getJobFromServer().pipe(
            switchMap((jobs) => {
              if (jobs && jobs.data.length > 0) {
                for (let job of jobs.data) {
                  if (job.attributes.slug === this.selectedJobSlug) {
                    return of(job);
                  }
                }
              }
              return of(null);
            }),
          );
        }
      }),
    );
  }

  private getJobFromServer(): Observable<JobsDto> {
    const query = `locale=${this.activeLanguage}&populate=${this.companyLogo}&populate=${this.jobCategories}&filters[slug][$eq]=${this.selectedJobSlug}`;
    return this._pageService.getJobs(query);
  }

  public initJobSEOContent(selectedJob: RawFormJobObject) {
    const urlFragments = this._router.url.split('/');
    if (urlFragments && urlFragments.length > 1 && ['en', 'fa', 'de'].includes(urlFragments[1])) {
      const language = urlFragments[1];

      this.activatedRoute.params.subscribe((params) => {
        const { title, company_name, location, qualification, slug } = selectedJob.attributes;
        const baseUrl = `https://almanjobs.com/${language}/job/${slug}`;
        const imageUrl = 'https://almanjobs-strapi-public-bucket.s3-eu-central-2.ionoscloud.com/almanjobs_logo_primary_98d1b23517.svg';

        // Common meta tags
        this.metaService.updateTag({ name: 'robots', content: 'index, follow' });
        this.metaService.updateTag({ name: 'viewport', content: 'width=device-width, initial-scale=1.0' });
        this.metaService.updateTag({ name: 'author', content: 'Almanjobs' });
        this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
        this.metaService.updateTag({ name: 'og:image', content: imageUrl });
        this.metaService.updateTag({ name: 'twitter:image', content: imageUrl });
        this.metaService.addTag({ rel: 'alternate', hreflang: 'x-default', href: baseUrl });

        // Language-specific meta tags
        if (language === 'en') {
          this.titleService.setTitle(`${title} at ${company_name} - Apply Now | Almanjobs`);
          this.metaService.updateTag({
            name: 'description',
            content: `Apply for the position of ${title} at ${company_name} located in ${location}. ${qualification.substring(0, 150)}...`,
          });
          this.metaService.updateTag({ name: 'keywords', content: `job in ${location}, ${title} jobs, work at ${company_name}, apply for ${title}` });
          this.metaService.addTag({ rel: 'alternate', hreflang: 'en', href: baseUrl });
        }

        if (language === 'de') {
          this.titleService.setTitle(`${title} bei ${company_name} - Jetzt bewerben | Almanjobs`);
          this.metaService.updateTag({
            name: 'description',
            content: `Bewerben Sie sich für die Position ${title} bei ${company_name} in ${location}. ${qualification.substring(0, 150)}...`,
          });
          this.metaService.updateTag({ name: 'keywords', content: `Job in ${location}, ${title} Jobs, arbeiten bei ${company_name}, bewerben für ${title}` });
          this.metaService.addTag({ rel: 'alternate', hreflang: 'de', href: baseUrl });
        }

        if (language === 'fa') {
          this.titleService.setTitle(`${title} در ${company_name} - هم اکنون اقدام کنید | Almanjobs`);
          this.metaService.updateTag({
            name: 'description',
            content: `برای موقعیت ${title} در ${company_name} واقع در ${location} اقدام کنید. ${qualification.substring(0, 150)}...`,
          });
          this.metaService.updateTag({ name: 'keywords', content: `شغل در ${location}, شغل‌های ${title}, کار در ${company_name}, اقدام برای ${title}` });
          this.metaService.addTag({ rel: 'alternate', hreflang: 'fa', href: baseUrl });
        }
      });
    }
  }

}
